import { useMemo } from 'react'
import { View } from 'react-native'
import Svg, { Path } from 'react-native-svg'

const VIEWBOX_WIDTH = 390
const VIEWBOX_HEIGHT = 138

interface WaveLayerConfig {
  count: number
  color: string
  startY: number
  spacing: number
  baseAmplitude: number
  amplitudeVariance: number
  frequency: number
  phaseShift: number
  opacity: number
}

interface PostPaymentStepOneWaveProps {
  width: number
  height: number
  className?: string
}

function buildWavePath(
  baseline: number,
  amplitude: number,
  frequency: number,
  phase: number,
): string {
  const points = 80
  let d = `M 0 ${baseline.toFixed(2)}`

  for (let i = 1; i <= points; i += 1) {
    const progress = i / points
    const x = progress * VIEWBOX_WIDTH
    const y =
      baseline +
      Math.sin(progress * Math.PI * 5 * frequency + phase) * amplitude +
      Math.sin(progress * Math.PI * 11 + phase * 0.7) * (amplitude * 0.34) +
      Math.cos(progress * Math.PI * 3 + phase * 1.9) * (amplitude * 0.2)

    d += ` L ${x.toFixed(2)} ${y.toFixed(2)}`
  }

  return d
}

function buildLayerPaths(config: WaveLayerConfig) {
  return Array.from({ length: config.count }, (_, index) => {
    const baseline = config.startY + index * config.spacing
    const amplitude =
      config.baseAmplitude + Math.sin(index * 0.65) * config.amplitudeVariance
    const phase = index * config.phaseShift

    return {
      key: `${config.color}-${index}`,
      d: buildWavePath(baseline, amplitude, config.frequency, phase),
      color: config.color,
      opacity: Math.max(0.16, config.opacity - index * 0.01),
    }
  })
}

export function PostPaymentStepOneWave({
  width,
  height,
  className,
}: PostPaymentStepOneWaveProps) {
  const wavePaths = useMemo(() => {
    const layers: WaveLayerConfig[] = [
      {
        count: 22,
        color: '#291180',
        startY: 44,
        spacing: 1.8,
        baseAmplitude: 12,
        amplitudeVariance: 3.5,
        frequency: 1.04,
        phaseShift: 0.23,
        opacity: 0.42,
      },
      {
        count: 20,
        color: '#FFB104',
        startY: 66,
        spacing: 1.8,
        baseAmplitude: 9.2,
        amplitudeVariance: 2.8,
        frequency: 1.18,
        phaseShift: 0.2,
        opacity: 0.38,
      },
      {
        count: 21,
        color: '#E30B5D',
        startY: 88,
        spacing: 1.8,
        baseAmplitude: 10,
        amplitudeVariance: 2.4,
        frequency: 1.24,
        phaseShift: 0.2,
        opacity: 0.33,
      },
    ]

    return layers.flatMap((layer) => buildLayerPaths(layer))
  }, [])

  return (
    <View className={className}>
      <Svg
        width={width}
        height={height}
        viewBox={`0 0 ${VIEWBOX_WIDTH} ${VIEWBOX_HEIGHT}`}
        fill="none"
      >
        {wavePaths.map((wavePath) => (
          <Path
            key={wavePath.key}
            d={wavePath.d}
            stroke={wavePath.color}
            strokeOpacity={wavePath.opacity}
            strokeWidth={1}
            fill="none"
          />
        ))}
      </Svg>
    </View>
  )
}
